import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { getCategory } from "@/lib/eliteCategoryStyle";
import { TL, localeUI } from "@/lib/prekladyUI";
import { sJednotkou } from "@/lib/castka";

/**
 * HouseholdGraph — animovaný SVG graf domácnosti (Elite kit, vlna 3, modul 1).
 *
 * Poskládání dle reference (design-kit-export HouseholdGraph.jsx → GraphCanvas):
 *   • Hub uprostřed (jméno rodiny/klienta + „DOMÁCNOST", fialový gradient),
 *     klient je hlava domácnosti.
 *   • Členové na HORNÍM OBLOUKU (semicircle −π až 0), ne na plné kružnici.
 *   • Uzly členů: foto (clipPath) nebo monogram, kroužek v barvě kategorie
 *     (ringHex; člen z /api/family kategorii nemá → padne na C/neutrál, proto
 *     přimícháme barvu role), NAD uzlem jméno (Outfit) + role uppercase,
 *     POD uzlem AUM odznak (bílá pilulka) — pokud AUM chybí, odznak vynecháme.
 *   • Spojnice = motion.line (pathLength), uzly naskakují spring se staggerem.
 *   • POD grafem seznam členů (2 sloupce) s levým accent barem dle kategorie.
 *
 * Barvy jedou přes CSS tokeny (--accent/--ink/--muted/--surface/--line) → funguje
 * ve všech 3 tématech; kategorické/rolové barvy jsou theme-nezávislé (jako drahokamy).
 *
 * Data/logika Beplanu jsou svaté: props, testidy (household-graph,
 * household-node-{id}, household-add-member) i onAdd/onPickMember zůstávají.
 */

const ROLE_LABELS = {
  spouse: "Manžel/ka",
  partner: "Partner/ka",
  child: "Dítě",
  parent: "Rodič",
  sibling: "Sourozenec",
  head: "Hlava rodiny",
  other: "Vazba",
};

// Barvy rolí — kroužek + spojnice člena. Kategorické tokeny motivu (--akt-*, --skup-*),
// dřív výchozí Tailwind hexy, které se motivem neřídily (CRM-09). Kreslí se přes style,
// ne přes SVG atribut (var() v atributu nefunguje).
const ROLE_COLORS = {
  spouse: "var(--akt-email)",
  partner: "var(--skup-penze)",
  child: "var(--akt-dopis)",
  parent: "var(--akt-schuzka)",
  sibling: "var(--skup-investice)",
  head: "var(--accent)",
  other: "var(--akt-ukol)",
};

// Řazení členů pro stabilní layout na oblouku (partner blíž středu, děti dál).
const ROLE_ORDER = { head: 0, spouse: 1, partner: 1, child: 2, parent: 3, sibling: 4, other: 9 };

// Robustnost: `members` může přijít jako ne-pole nebo s null-položkami.
// [...members] nad ne-iterovatelnou hodnotou spadne → asArr vrátí čisté pole objektů.
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

/* Písmo v SVG (jednotky viewBoxu 800 × 360/460) — stupnice appky: popisky
 * VELKÝMI 11 (text-popisek), běžný text min. 12. Dřív 8,5–10,5 → nečitelné,
 * zvlášť když se plátno zmenší na šířku karty. Hlídá HouseholdGraph.pismo.test.js. */
export const PISMO = { popisek: 11, jmeno: 12, jmenoVelke: 14 };

/* Šířka pilulky AUM podle délky částky (12 px tučně ≈ 7,4 px na znak + okraje),
 * ať „12 345 678 Kč" nepřeteče přes okraj. */
export const sirkaPilulky = (text) => Math.max(92, Math.ceil(String(text || "").length * 7.4 + 22));

/* Rozložení stromu — čistá funkce (testovatelná bez DOM).
 * Členové leží na horní půl-ELIPSE: vodorovná poloosa roste s počtem uzlů
 * (písmo 11–14 = širší popisky → uzly se nesmí překrývat), svislá je omezená,
 * aby jméno nad nejvyšším uzlem zůstalo uvnitř plátna. Krajní uzly jsou
 * zvednuté o 0,2 rad nad úroveň hubu (jinak by se sousedé u kraje skládali
 * pod sebe). Od 6 slotů plátno roste o 50 (velké 60) na slot a pilulky AUM se v grafu
 * nekreslí (částka je u každého člena v seznamu pod grafem) — ověřeno bez
 * překryvu do 8 členů (HouseholdGraph.pismo.test.js). */
export const MAX_SLOTU_S_PILULKOU = 5;
export function rozlozeniDomacnosti(slots, large = false) {
  const n = Math.max(1, slots);
  const navic = Math.max(0, n - MAX_SLOTU_S_PILULKOU) * (large ? 60 : 50);
  const W = 800;
  const H = (large ? 460 : 360) + navic;
  const CX = W / 2;
  const CY = (large ? 276 : 210) + navic;
  const HUB_R = large ? 52 : 44;
  const NODE_R = large ? 36 : 26;
  // nad uzlem: role 11 px + jméno 12/14 px (baseline NODE_R + 19) → ~34 px rezervy
  const RY = Math.min(CY - NODE_R - 36, (large ? 150 : 96) + n * (large ? 16 : 11) + navic);
  // do stran: popisek ~ 100 px, pilulka AUM ~ 120 px → nechat 66 px od okraje
  const RX = Math.min(CX - 66, Math.max(RY, (large ? 170 : 130) + n * (large ? 34 : 40)));
  const okraj = n <= 2 ? 0 : 0.2;
  const positions = Array.from({ length: n }, (_, i) => {
    if (n === 1) return { x: CX, y: CY - RY };
    const angle = -Math.PI + okraj + ((Math.PI - 2 * okraj) * i) / (n - 1);
    return { x: CX + RX * Math.cos(angle), y: CY + RY * Math.sin(angle) };
  });
  return { W, H, CX, CY, HUB_R, NODE_R, RX, RY, positions, pilulky: n <= MAX_SLOTU_S_PILULKOU };
}

export default function HouseholdGraph({
  centerName = "Klient",
  centerPhoto = "",
  members = [],
  onAdd,
  onPickMember,
  large = false,
}) {
  // Řazení + „přidat" jako poslední slot na oblouku.
  const safeMembers = asArr(members);
  const sorted = [...safeMembers].sort(
    (a, b) => (ROLE_ORDER[a.household_role] ?? 9) - (ROLE_ORDER[b.household_role] ?? 9)
  );

  // SVG plátno — členové na horním oblouku, hub níž uprostřed.
  const slots = sorted.length + 1; // +1 dlaždice „přidat"
  const { W, H, CX, CY, HUB_R, NODE_R, positions, pilulky } = rozlozeniDomacnosti(slots, large);

  const totalAum = safeMembers.reduce((s, m) => s + (Number(m.signed_bj) || 0), 0);

  return (
    <div
      className="relative p-4 lg:p-6 overflow-hidden rounded-2xl elite-shadow ring-hairline"
      style={{ background: "var(--surface)" }}
      data-testid="household-graph"
    >
      {/* hlavička sekce grafu */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-popisek uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--accent)" }}>
            {TL("Rodinný strom")} · {safeMembers.length}{" "}
            {TL(safeMembers.length === 1 ? "vazba" : safeMembers.length >= 2 && safeMembers.length <= 4 ? "vazby" : "vazeb")}
          </div>
          <h2 className="font-display text-[18px] lg:text-[22px] font-semibold tracking-tight mt-0.5" style={{ color: "var(--ink)" }}>
            {TL("Domácnost")} {firstName(centerName)}
          </h2>
        </div>
        {totalAum > 0 && (
          <div className="rounded-xl px-3 py-2" style={{ background: "var(--surface-2, rgba(0,0,0,0.03))" }}>
            <div className="text-popisek uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--muted)" }}>
              {TL("Podepsáno celkem")}
            </div>
            <div className="font-display font-semibold text-[15px] tabular-nums mono" style={{ color: "var(--ink)" }}>
              {fmtBj(totalAum)}
            </div>
          </div>
        )}
      </div>

      {/* SVG plátno */}
      <div
        className="relative w-full overflow-hidden rounded-2xl ring-hairline"
        style={{ background: "var(--surface-2, rgba(0,0,0,0.02))" }}
      >
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" style={{ maxHeight: (large ? 520 : 400) + (H - (large ? 460 : 360)) }}>
          <defs>
            <radialGradient id="hg-bg-glow" cx="50%" cy="72%" r="60%">
              <stop offset="0%" style={{ stopColor: "var(--accent)" }} stopOpacity="0.10" />
              <stop offset="100%" style={{ stopColor: "var(--accent)" }} stopOpacity="0" />
            </radialGradient>
            <linearGradient id="hg-hub" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" style={{ stopColor: "var(--accent)" }} />
              <stop offset="100%" style={{ stopColor: "var(--accent-ink)" }} />
            </linearGradient>
            <clipPath id="hg-hub-clip">
              <circle cx={CX} cy={CY} r={HUB_R - 3} />
            </clipPath>
            {positions.map((p, i) =>
              i < sorted.length ? (
                <clipPath id={`hg-clip-${sorted[i].id || i}`} key={`clip-${i}`}>
                  <circle cx={p.x} cy={p.y} r={NODE_R - 4} />
                </clipPath>
              ) : null
            )}
          </defs>

          <rect width={W} height={H} fill="url(#hg-bg-glow)" />

          {/* spojnice hub → uzel (kreslí se postupně) */}
          {positions.map((p, i) => {
            const isAdd = i >= sorted.length;
            const color = isAdd ? "var(--line)" : ROLE_COLORS[sorted[i]?.household_role] || "var(--accent)";
            return (
              <motion.line
                key={`line-${i}`}
                x1={CX}
                y1={CY}
                x2={p.x}
                y2={p.y}
                style={{ stroke: color }}
                strokeWidth={isAdd ? 1 : 1.6}
                strokeLinecap="round"
                strokeDasharray={isAdd ? "3 4" : "none"}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: isAdd ? 0.55 : 0.85 }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.05 }}
              />
            );
          })}

          {/* HUB uprostřed — klient / hlava domácnosti */}
          <motion.g
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            style={{ transformOrigin: `${CX}px ${CY}px` }}
          >
            <circle cx={CX} cy={CY} r={HUB_R + 16} fill="url(#hg-hub)" opacity="0.16" />
            <circle cx={CX} cy={CY} r={HUB_R} fill="url(#hg-hub)" style={{ stroke: "var(--surface)" }} strokeWidth="3" />
            {centerPhoto ? (
              <image
                href={centerPhoto}
                x={CX - (HUB_R - 3)}
                y={CY - (HUB_R - 3)}
                width={(HUB_R - 3) * 2}
                height={(HUB_R - 3) * 2}
                clipPath="url(#hg-hub-clip)"
                preserveAspectRatio="xMidYMid slice"
              />
            ) : (
              <text x={CX} y={CY - 3} textAnchor="middle" fontSize={large ? 18 : 16} fontWeight="700" style={{ fill: "var(--on-accent)" }}>
                {initials(centerName)}
              </text>
            )}
            {!centerPhoto && (
              <text
                x={CX}
                y={CY + 16}
                textAnchor="middle"
                fontSize={PISMO.popisek}
                fontWeight="700"
                style={{ fill: "var(--on-accent)", letterSpacing: "0.08em", opacity: 0.9 }}
              >
                DOMÁCNOST
              </text>
            )}
            <text
              x={CX}
              y={CY + HUB_R + 18}
              textAnchor="middle"
              fontSize={large ? PISMO.jmenoVelke : PISMO.jmeno}
              fontWeight="700"
              style={{ fill: "var(--ink)", fontFamily: "var(--font-display)" }}
            >
              {firstName(centerName)}
            </text>
            <text
              x={CX}
              y={CY + HUB_R + 32}
              textAnchor="middle"
              fontSize={PISMO.popisek}
              fontWeight="700"
              style={{ fill: "var(--muted)", letterSpacing: "0.08em" }}
            >
              HLAVA RODINY
            </text>
          </motion.g>

          {/* uzly členů + dlaždice přidat */}
          {positions.map((p, i) => {
            // dlaždice „+ Přidat člena"
            if (i >= sorted.length) {
              return (
                <motion.g
                  key="add"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 22, delay: 0.25 + i * 0.06 }}
                  style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                  whileHover={{ scale: 1.1 }}
                  onClick={() => onAdd?.()}
                  data-testid="household-add-member"
                  cursor="pointer"
                >
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={NODE_R - 6}
                    style={{ fill: "var(--surface-2, rgba(0,0,0,0.03))", stroke: "var(--accent)" }}
                    opacity="0.9"
                    strokeDasharray="3 3"
                    strokeWidth="1.3"
                  />
                  <foreignObject x={p.x - 12} y={p.y - 12} width="24" height="24" style={{ pointerEvents: "none" }}>
                    <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--accent)" }}>
                      <Plus size={16} strokeWidth={2.2} />
                    </div>
                  </foreignObject>
                  <text
                    x={p.x}
                    y={p.y - NODE_R - 4}
                    textAnchor="middle"
                    fontSize={PISMO.popisek}
                    style={{ fill: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}
                  >
                    Přidat
                  </text>
                </motion.g>
              );
            }

            const m = sorted[i];
            const color = ROLE_COLORS[m.household_role] || getCategory(m).ringHex || "var(--akt-ukol)";
            const roleLabel = roleLabelFor(m);
            const memberAum = Number(m.signed_bj) || 0;
            const clipId = `hg-clip-${m.id || i}`;
            return (
              <motion.g
                key={m.id || i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 22, delay: 0.25 + i * 0.06 }}
                style={{ transformOrigin: `${p.x}px ${p.y}px`, cursor: "pointer" }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => onPickMember?.(m)}
                data-testid={`household-node-${m.id}`}
              >
                {/* barevné halo role (dýchá) */}
                <motion.circle
                  cx={p.x}
                  cy={p.y}
                  r={NODE_R + 3}
                  style={{ fill: color, transformOrigin: `${p.x}px ${p.y}px` }}
                  opacity="0.14"
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ duration: 2 + i * 0.35, repeat: Infinity, ease: "easeInOut" }}
                />
                <circle cx={p.x} cy={p.y} r={NODE_R} style={{ fill: "var(--surface)" }} />
                {m.avatar || m.photo_url ? (
                  <image
                    href={m.avatar || m.photo_url}
                    x={p.x - (NODE_R - 4)}
                    y={p.y - (NODE_R - 4)}
                    width={(NODE_R - 4) * 2}
                    height={(NODE_R - 4) * 2}
                    clipPath={`url(#${clipId})`}
                    preserveAspectRatio="xMidYMid slice"
                  />
                ) : (
                  <text x={p.x} y={p.y + 5} textAnchor="middle" fontSize={large ? PISMO.jmenoVelke : PISMO.jmeno} fontWeight="600" style={{ fill: "var(--ink)" }}>
                    {initials(m.name)}
                  </text>
                )}
                <circle cx={p.x} cy={p.y} r={NODE_R} fill="none" style={{ stroke: color }} strokeWidth="2.5" />

                {/* NAD uzlem: jméno + role uppercase */}
                <text
                  x={p.x}
                  y={p.y - NODE_R - 19}
                  textAnchor="middle"
                  fontSize={large ? PISMO.jmenoVelke : PISMO.jmeno}
                  fontWeight="700"
                  style={{ fill: "var(--ink)", fontFamily: "var(--font-display)" }}
                >
                  {firstName(m.name)}
                </text>
                <text
                  x={p.x}
                  y={p.y - NODE_R - 5}
                  textAnchor="middle"
                  fontSize={PISMO.popisek}
                  fontWeight="700"
                  style={{ fill: color, textTransform: "uppercase", letterSpacing: "0.08em" }}
                >
                  {roleLabel}
                </text>

                {/* POD uzlem: AUM odznak (bílá pilulka) — jen když je hodnota */}
                {pilulky && memberAum > 0 && (
                  <>
                    <rect
                      x={p.x - sirkaPilulky(fmtBj(memberAum)) / 2}
                      y={p.y + NODE_R + 6}
                      width={sirkaPilulky(fmtBj(memberAum))}
                      height="24"
                      rx="12"
                      style={{ fill: "var(--surface)", stroke: "var(--line)" }}
                      strokeWidth="1"
                    />
                    <text
                      x={p.x}
                      y={p.y + NODE_R + 22}
                      textAnchor="middle"
                      fontSize={PISMO.jmeno}
                      fontWeight="700"
                      style={{ fill: "var(--ink)" }}
                    >
                      {fmtBj(memberAum)}
                    </text>
                  </>
                )}
              </motion.g>
            );
          })}
        </svg>
      </div>

      {/* Seznam členů POD grafem — 2 sloupce, levý accent bar dle kategorie */}
      {sorted.length > 0 && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {sorted.map((m) => {
            const cat = getCategory(m);
            const roleColor = ROLE_COLORS[m.household_role] || cat.ringHex;
            const memberAum = Number(m.signed_bj) || 0;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onPickMember?.(m)}
                data-testid={`household-member-card-${m.id}`}
                className="relative flex items-center gap-3 p-3 pl-4 rounded-2xl elite-shadow ring-hairline text-left transition-shadow hover:elite-shadow-strong overflow-hidden"
                style={{ background: "var(--surface)" }}
              >
                <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-1 ${cat.accentBar}`} />
                <div
                  className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center shrink-0 font-semibold text-[13px]"
                  style={{ background: "var(--surface-2, rgba(0,0,0,0.04))", color: "var(--ink)", boxShadow: `0 0 0 2px color-mix(in srgb, ${roleColor} 33%, transparent)` }}
                >
                  {m.avatar || m.photo_url ? (
                    <img src={m.avatar || m.photo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    initials(m.name)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate" style={{ color: "var(--ink)" }}>
                    {m.name}
                  </div>
                  <div className="text-drobny truncate" style={{ color: "var(--muted)" }}>
                    {roleLabelFor(m)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-popisek uppercase tracking-[0.12em] font-semibold px-2 h-5 inline-flex items-center rounded-full ${cat.chip}`}>
                    {cat.label}
                  </span>
                  <div className="text-drobny tabular-nums font-semibold mt-1 mono" style={{ color: "var(--ink)" }}>
                    {memberAum > 0 ? fmtBj(memberAum) : "—"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────

/* Hodnota člena jsou PODEPSANÉ BJ (signed_bj), ne peníze. Dřív se formátovala
 * jako koruny (Intl currency CZK) → „80 Kč" místo „80 BJ", u SK navíc cizí měna
 * (nález CRM-SK-05, 18. 10. 2026). Pole `aum` žádný backend neplní, proto pryč. */
const fmtBj = (n) =>
  sJednotkou(new Intl.NumberFormat(localeUI(), { maximumFractionDigits: 1 }).format(Number(n) || 0), "BJ");

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function firstName(name) {
  if (!name) return "";
  const fn = name.trim().split(/\s+/)[0] || "";
  return fn.length > 12 ? fn.slice(0, 11) + "…" : fn;
}

// Ženská koncovka jména (heuristika jen pro label manžel/manželka, syn/dcera).
// Čistě prezentační — data se nemění; při nejistotě padá na neutrální label.
function isLikelyFemale(name) {
  const fn = (name || "").trim().split(/\s+/)[0] || "";
  return /(a|e)$/i.test(fn);
}

function roleLabelFor(m) {
  const role = m.household_role;
  if (role === "spouse") return TL(isLikelyFemale(m.name) ? "Manželka" : "Manžel");
  if (role === "child") return TL(isLikelyFemale(m.name) ? "Dcera" : "Syn");
  return TL(ROLE_LABELS[role] || role || "Vazba");
}
